/**
 * @file index.tsx
 *
 * @fileoverview Shows job recommendations cards in the grid.
 * Handles error and loading state.
 */
import { FormattedMessage } from 'react-intl';
import BookmarkedJobsProvider from '~/jobiqo/components/BookmarkedJobs/context';
import JobTeaserCard from '~/jobiqo/components/Job/Teaser';
import { Job } from '~/jobiqo/types/types';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { JobTeaserPlaceholder } from '~/vcl/organisms/05-placeholders/03-job/03-teaser';
import { Text } from '~/vcl/atoms/13-text/03-text';

export const JOB_RECOMMENDER_JOBS_PER_PAGE = 6;

export interface JobRecommenderLayoutProps {
  /**
   * The recommended job items.
   */
  jobs: Job[];
  /**
   * The title.
   *
   */
  Title?: React.ReactNode;
  /**
   * The description.
   *
   */
  Description?: React.ReactNode;
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The error flag.
   */
  error: boolean;
  /**
   * The jobs per page amount.
   *
   * @default JOB_RECOMMENDER_JOBS_PER_PAGE
   */
  jobsPerPage?: number;
}

export default function JobRecommenderLayout({
  Title = (
    <FormattedMessage
      id="job_recommender_default_title"
      defaultMessage="Similar jobs"
    />
  ),
  Description = (
    <FormattedMessage
      id="job_recommender_default_description"
      defaultMessage="Meanwhile, you can browse through similar jobs - maybe the next big step is already waiting for you."
    />
  ),
  jobs,
  loading,
  error,
  jobsPerPage = JOB_RECOMMENDER_JOBS_PER_PAGE,
}: JobRecommenderLayoutProps) {
  // Do not show recommendations if we get an error
  // or we don't have any similar jobs.
  if (error || jobs?.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <>
        <Grid columns={[1, 2]}>
          {Array.apply(null, Array(jobsPerPage)).map((_, index: number) => (
            <Cell key={index}>
              <JobTeaserPlaceholder />
            </Cell>
          ))}
        </Grid>
      </>
    );
  }

  return (
    <BookmarkedJobsProvider
      initialState={jobs.filter((job: Job) => job?.bookmarked) ?? []}
    >
      <Spacing paddingTop={6}>
        <h2>{Title}</h2>
        <Text color="gray7" fontSize={2}>
          {Description}
        </Text>
        <Spacing size="medium" />
        <Grid columns={[1, 2]}>
          {jobs.map(
            (job: Job) =>
              job && (
                <Cell display="grid" key={job.id}>
                  <JobTeaserCard job={job} />
                </Cell>
              ),
          )}
        </Grid>
      </Spacing>
    </BookmarkedJobsProvider>
  );
}

export { JobRecommenderLayout };
